import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Switch, Tooltip } from 'antd'
import styled from 'styled-components'
import { SyncOutlined } from '@ant-design/icons'
import { colours, sizes } from '@qflow/theme'

import { SubmissionErrorsDescriptionFilters } from 'portal/components/AlertsTable/InfractionTableHelpers'
import {
  ALERT_MAPPER,
  ALERT_STATUS_MAPPER
} from 'portal/components/RecordsModules/common/alerts'
import type { AlertStatusType } from 'portal/components/RecordsModules/common/alerts'
import { ProductGroupTag } from 'portal/components/RecordsModules/common/StyledComponents'
import { ALERT_TYPES } from 'portal/lib/constants/alertTypes'
import { useModuleIds } from 'portal/lib/hooks'
import { ColourButton, icons } from 'portal/lib/primitives'
import { handleApiFailure } from 'portal/lib/sharedComponents/apiFailureHandler'

import { dismissEvents } from 'portal/store/events/actions'
import { loadRecordInfractions } from 'portal/store/infractions/actions'
import { trackMixpanel } from 'portal/store/mixpanel/actions'
import { recordAlertStatusChanged } from 'portal/store/records/alerts/actions'

export function AlertsPanel({
  alerts,
  canEdit
}: {
  alerts: AlertType[]
  canEdit: boolean
}) {
  const [alertsState, setAlertsState] = useState<AlertType[]>([])
  const [alertsFiltered, setAlertsFiltered] = useState<AlertType[]>([])
  const [infractionsFiltered, setInfractionsFiltered] = useState<
    InfractionType[]
  >([])
  const [includeDismissied, setIncludeDismissed] = useState(false)

  const dispatch = useDispatch()
  const { accountId, projectId, moduleName, recordId } = useModuleIds()

  const INFRACTION_STATE = {
    ignored: 'Ignored',
    open: 'Open',
    resolved: 'Resolved',
    updating: 'Updating'
  } as const

  // Set alerts state when the component mounts
  useEffect(() => {
    if (!alerts || alerts.length === 0) {
      setAlertsState([])
      return
    }
    setAlertsState(alerts)
  }, [alerts])

  const infractionTitle = useMemo(() => {
    if (!moduleName) {
      return ''
    }
    if (moduleName === 'delivery') {
      return ALERT_TYPES.MATERIAL_CERTIFICATIONS
    }
    return ALERT_TYPES.WASTE_COMPLIANCE
  }, [moduleName])

  const infractionsObject: ReduxInfractionObject = useSelector(
    (state: any) => state.infractions?.record
  )

  useEffect(() => {
    // Filter alerts based on the includeDismissied state
    const filteredAlerts = alertsState.filter(alert => {
      // First, filter out unmapped alerts
      if (!ALERT_MAPPER[alert.recordAlertType]) {
        return false
      }
      // then filter based on the includeDismissied state
      if (includeDismissied) {
        return true
      }
      return (
        alert.status === ALERT_STATUS_MAPPER.active ||
        alert.status === ALERT_STATUS_MAPPER.updating
      )
    })

    // Group Alerts by recordAlertType, triggeredSetting and status. Sum their counts and convert recordAlertId to an array
    const groupedAlerts: { [key: string]: AlertType } = {}
    filteredAlerts.forEach(alert => {
      // Create a unique key based on eventDescriptor and alert status
      const key = `${alert.recordAlertType}||${alert.triggeredSetting || 'N/A'}||${alert.status}`

      if (!groupedAlerts[key]) {
        groupedAlerts[key] = {
          ...alert,
          count: 1
        }
      } else {
        groupedAlerts[key].count = (groupedAlerts[key].count || 1) + 1
        // Convert recordAlertId to an array of recordAlertId
        if (Array.isArray(groupedAlerts[key].recordAlertId)) {
          if (Array.isArray(alert.recordAlertId)) {
            groupedAlerts[key].recordAlertId.push(...alert.recordAlertId)
          } else {
            groupedAlerts[key].recordAlertId.push(alert.recordAlertId)
          }
        } else {
          groupedAlerts[key].recordAlertId = [
            groupedAlerts[key].recordAlertId as string,
            ...(Array.isArray(alert.recordAlertId)
              ? alert.recordAlertId
              : [alert.recordAlertId])
          ]
        }
      }
    })

    const filteredAlertsArray = Object.values(groupedAlerts)

    setAlertsFiltered(filteredAlertsArray)
  }, [alertsState, includeDismissied])

  useEffect(() => {
    // Filter infractions based on the includeDismissied state
    if (infractionsObject && infractionsObject.list) {
      const filteredInfractions = infractionsObject.list.filter(infraction => {
        if (includeDismissied) {
          return true
        }
        return infraction.status === INFRACTION_STATE.open
      })

      // Group infractions by eventDescriptor and status. Sum their counts and convert eventKey to an array
      const groupedInfractions: { [key: string]: InfractionType } = {}
      filteredInfractions.forEach(infraction => {
        // Create a unique key based on eventDescriptor and status
        const key = `${infraction.eventDescriptor}||${infraction.status}`

        if (!groupedInfractions[key]) {
          groupedInfractions[key] = {
            ...infraction,
            count: 1
          }
        } else {
          groupedInfractions[key].count =
            (groupedInfractions[key].count || 1) + 1
          // Convert eventKey to an array of eventKeys
          if (Array.isArray(groupedInfractions[key].eventKey)) {
            if (Array.isArray(infraction.eventKey)) {
              groupedInfractions[key].eventKey.push(...infraction.eventKey)
            } else {
              groupedInfractions[key].eventKey.push(infraction.eventKey)
            }
          } else {
            groupedInfractions[key].eventKey = [
              groupedInfractions[key].eventKey as string,
              ...(Array.isArray(infraction.eventKey)
                ? infraction.eventKey
                : [infraction.eventKey])
            ]
          }
        }
      })

      const filteredInfractionsArray = Object.values(groupedInfractions)

      setInfractionsFiltered(filteredInfractionsArray)
    }
  }, [includeDismissied, INFRACTION_STATE.open, infractionsObject])

  const handleAlertStatusChange = (
    alert: AlertType,
    status: AlertStatusType
  ) => {
    dispatch(
      trackMixpanel('frontend_records_alert_status_change', {
        account_id: accountId,
        project_id: projectId,
        record_type: moduleName,
        record_id: recordId,
        alert_id: alert.recordAlertId,
        alert_type: alert.recordAlertType,
        status: status
      })
    )

    const currentAlerts = [...alertsState]
    const updatingAlerts = [
      ...currentAlerts.map(individualAlert => {
        if (
          individualAlert.recordAlertId === alert.recordAlertId ||
          (Array.isArray(alert.recordAlertId) &&
            alert.recordAlertId.includes(
              individualAlert.recordAlertId as string
            ))
        ) {
          return {
            ...individualAlert,
            status: ALERT_STATUS_MAPPER.updating
          }
        }
        return individualAlert
      })
    ]
    setAlertsState(updatingAlerts)

    const updatedAlerts = [
      ...alertsState.map(individualAlert => {
        if (
          individualAlert.recordAlertId === alert.recordAlertId ||
          (Array.isArray(alert.recordAlertId) &&
            alert.recordAlertId.includes(
              individualAlert.recordAlertId as string
            ))
        ) {
          return {
            ...individualAlert,
            status: status
          }
        }
        return individualAlert
      })
    ]

    if (accountId && projectId && recordId) {
      // Using a timeout to allow the UI to update before making the API call
      setTimeout(() => {
        if (Array.isArray(alert.recordAlertId)) {
          alert.recordAlertId.forEach(id => {
            dispatch(
              recordAlertStatusChanged(
                accountId,
                projectId,
                recordId,
                id,
                status,
                () => {
                  // onSuccess
                  setAlertsState(updatedAlerts)
                },
                error => {
                  // onError
                  setAlertsState(currentAlerts)
                  handleApiFailure(error)
                }
              )
            )
          })
          return
        } else {
          dispatch(
            recordAlertStatusChanged(
              accountId,
              projectId,
              recordId,
              alert.recordAlertId,
              status,
              () => {
                // onSuccess
                setAlertsState(updatedAlerts)
              },
              error => {
                // onError
                setAlertsState(currentAlerts)
                handleApiFailure(error)
              }
            )
          )
        }
      }, 10)
    }
  }

  const handleInfractionStatusChange = (
    infraction: InfractionType,
    status: 'Open' | 'Resolved' | 'Ignored'
  ) => {
    dispatch(
      trackMixpanel('frontend_records_infraction_status_change', {
        account_id: accountId,
        project_id: projectId,
        record_type: moduleName,
        record_id: recordId,
        eventKey: infraction.eventKey,
        status: status
      })
    )

    const currentInfractions = [...infractionsFiltered]
    const updatingInfractionsFiltered = [
      ...currentInfractions.map(infractionFiltered => {
        if (infractionFiltered.eventKey === infraction.eventKey) {
          return {
            ...infraction,
            status: ALERT_STATUS_MAPPER.updating
          }
        }
        return infractionFiltered
      })
    ]
    setInfractionsFiltered(updatingInfractionsFiltered)

    // Using a timeout to allow the UI to update before making the API call
    setTimeout(() => {
      if (accountId && projectId && recordId) {
        dispatch(
          dismissEvents(
            accountId,
            projectId,
            Array.isArray(infraction.eventKey)
              ? infraction.eventKey
              : [infraction.eventKey],
            reloadInfractions, // onSuccess
            error => {
              // onError
              setInfractionsFiltered(currentInfractions)
              handleApiFailure(error)
            }
          )
        )
      }
    }, 10)
  }

  // Reload infractions after a short delay to ensure the backend has processed the dismissal
  const reloadInfractions = () => {
    setTimeout(() => {
      dispatch(
        loadRecordInfractions(accountId, projectId, moduleName, recordId)
      )
    }, 500)
  }

  const ChangeAlertStatusButton = (alert: AlertType, canEdit: boolean) => {
    const ALERT_TEXT =
      alert.count && alert.count > 1 ? `${alert.count} alerts` : 'alert'

    if (canEdit) {
      return alert.status === ALERT_STATUS_MAPPER.clientDismissed ? (
        <Tooltip title={`Restore ${ALERT_TEXT}`}>
          <ColourButton.Basic
            aria-label={`Restore ${ALERT_TEXT}`}
            style={{ padding: '4px 7px', minHeight: '1.75rem' }}
            onClick={() =>
              handleAlertStatusChange(alert, ALERT_STATUS_MAPPER.active)
            }
          >
            <icons.Restore colour="currentColor" size={sizes.X_SMALL} />
          </ColourButton.Basic>
        </Tooltip>
      ) : alert.status === ALERT_STATUS_MAPPER.updating ? (
        <Tooltip title={`Dismissing ${ALERT_TEXT}`}>
          <ColourButton.NegativeSecondary
            disabled={true}
            style={{ padding: '2px 6px', minHeight: '1.75rem' }}
          >
            <SyncOutlined spin />
          </ColourButton.NegativeSecondary>
        </Tooltip>
      ) : (
        <Tooltip title={`Dismiss ${ALERT_TEXT}`}>
          <ColourButton.NegativeSecondary
            aria-label={`Dismiss ${ALERT_TEXT}`}
            style={{ padding: '2px 7px', minHeight: '1.75rem' }}
            onClick={() =>
              handleAlertStatusChange(
                alert,
                ALERT_STATUS_MAPPER.clientDismissed
              )
            }
          >
            <icons.TrashCan colour="currentColor" size={sizes.X_SMALL} />
          </ColourButton.NegativeSecondary>
        </Tooltip>
      )
    } else {
      return <div />
    }
  }

  const ChangeInfractionStatusButton = (
    infraction: InfractionType,
    canEdit: boolean
  ) => {
    const ALERT_TEXT =
      infraction.count && infraction.count > 1
        ? `${infraction.count} alerts`
        : 'alert'

    if (canEdit) {
      return infraction.status !== INFRACTION_STATE.open &&
        infraction.status !== INFRACTION_STATE.updating ? (
        <div />
      ) : infraction.status === INFRACTION_STATE.updating ? (
        <Tooltip title={`Dismissing ${ALERT_TEXT}`}>
          <ColourButton.NegativeSecondary
            disabled={true}
            style={{ padding: '2px 6px', minHeight: '1.75rem' }}
          >
            <SyncOutlined spin />
          </ColourButton.NegativeSecondary>
        </Tooltip>
      ) : (
        <Tooltip title={`Dismiss ${ALERT_TEXT}`}>
          <ColourButton.NegativeSecondary
            aria-label={`Dismiss ${ALERT_TEXT}`}
            style={{ padding: '2px 7px', minHeight: '1.75rem' }}
            onClick={() =>
              handleInfractionStatusChange(infraction, INFRACTION_STATE.ignored)
            }
          >
            <icons.TrashCan colour="currentColor" size={sizes.X_SMALL} />
          </ColourButton.NegativeSecondary>
        </Tooltip>
      )
    } else {
      return <div />
    }
  }

  const isSubmissionIssue = (infraction: any) =>
    SubmissionErrorsDescriptionFilters.some(filter =>
      infraction.eventDescriptor.startsWith(filter.value)
    )

  return (
    <MultiColumnRow style={{ width: '25rem', flexGrow: 0 }}>
      <SubSection
        style={{ marginTop: '1.25rem', height: 'calc(100% - 1.25rem)' }}
      >
        <SubSectionHeadingWrapper>
          <SubSectionHeading>Alerts</SubSectionHeading>
          <ShowDismissedWrapper>
            Include dismissed
            <Switch
              size={'small'}
              checked={includeDismissied}
              onChange={() => {
                setIncludeDismissed(prev => !prev)
              }}
            />
          </ShowDismissedWrapper>
        </SubSectionHeadingWrapper>
        {alertsFiltered.length === 0 &&
          (!infractionsFiltered || infractionsFiltered.length === 0) && (
            <NoAlertsPanel>No active alerts</NoAlertsPanel>
          )}
        {(alertsFiltered.length > 0 ||
          (infractionsFiltered && infractionsFiltered.length > 0)) && (
          <AlertGroupPanel>
            {alertsFiltered.length > 0 && (
              <>
                {alertsFiltered.map((alert, index) => {
                  const { type, description } =
                    ALERT_MAPPER[alert.recordAlertType]
                  return (
                    <Alert key={index}>
                      <AlertRule
                        $dismissed={
                          alert.status === ALERT_STATUS_MAPPER.clientDismissed
                        }
                      >
                        <div>{type}</div>
                        {ChangeAlertStatusButton(alert, canEdit)}
                      </AlertRule>
                      <AlertDescription
                        $dismissed={
                          alert.status === ALERT_STATUS_MAPPER.clientDismissed
                        }
                      >
                        {description}
                        {alert.triggeredSetting ? (
                          <>
                            :{' '}
                            <CustomMessage>
                              {alert.triggeredSetting}
                            </CustomMessage>
                          </>
                        ) : (
                          ''
                        )}
                      </AlertDescription>
                      {alert.count && alert.count > 1 && (
                        <AlertDescription
                          $dismissed={
                            alert.status !== ALERT_STATUS_MAPPER.clientDismissed
                          }
                          style={{ marginTop: '0.5rem' }}
                        >
                          <ProductGroupTag>
                            {alert.count &&
                              alert.count > 1 &&
                              ` x${alert.count}`}
                          </ProductGroupTag>
                        </AlertDescription>
                      )}
                    </Alert>
                  )
                })}
              </>
            )}
            {infractionsFiltered && infractionsFiltered.length > 0 && (
              <>
                {infractionsFiltered.map((infraction: any, index: number) => (
                  <Alert key={index}>
                    <AlertRule
                      $dismissed={
                        infraction.status !== INFRACTION_STATE.open &&
                        infraction.status !== INFRACTION_STATE.updating
                      }
                    >
                      <div>
                        {isSubmissionIssue(infraction)
                          ? ALERT_TYPES.SUBMISSION_ISSUES
                          : infractionTitle}
                      </div>
                      {ChangeInfractionStatusButton(infraction, canEdit)}
                    </AlertRule>
                    <AlertDescription
                      $dismissed={
                        infraction.status !== INFRACTION_STATE.open &&
                        infraction.status !== INFRACTION_STATE.updating
                      }
                    >
                      {infraction.eventDescriptor}
                    </AlertDescription>
                    {infraction.count && infraction.count > 1 && (
                      <AlertDescription
                        $dismissed={
                          infraction.status !== INFRACTION_STATE.open &&
                          infraction.status !== INFRACTION_STATE.updating
                        }
                        style={{ marginTop: '0.5rem' }}
                      >
                        <ProductGroupTag>
                          {infraction.count &&
                            infraction.count > 1 &&
                            ` x${infraction.count}`}
                        </ProductGroupTag>
                      </AlertDescription>
                    )}
                  </Alert>
                ))}
              </>
            )}
          </AlertGroupPanel>
        )}
      </SubSection>
    </MultiColumnRow>
  )
}

type AlertType = {
  recordAlertId: string | string[]
  recordAlertType: string
  triggeredSetting?: string
  lineitemId: string | null
  status: AlertStatusType
  count?: number
}

type InfractionType = {
  infractionId: string
  eventDescriptor: string
  eventKey: string | string[]
  status: string
  fieldName?: string
  notes?: string
  count?: number
}

type ReduxInfractionObject = {
  list: InfractionType[]
  byFieldName: { [key: string]: InfractionType }
}

const MultiColumnRow = styled.div`
  flex-grow: 1;
`
const SubSection = styled.div`
  padding: 1rem 1.25rem 1.125rem;
  background-color: ${colours.CONTROL_PANEL_GREY};
  border-radius: 10px;
  margin-top: 0.625rem;
`
const SubSectionHeadingWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`
const SubSectionHeading = styled.h3`
  color: ${colours.CORE_NAVY};
  font-size: 0.9375rem;
  font-family: 'Jakarta Sans', 'Roboto', sans-serif !important;
  font-weight: 700;
  line-height: 1.375rem;
`
const ShowDismissedWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
`
const NoAlertsPanel = styled.div`
  margin-top: 1.25rem;
  padding: 0.75rem 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${colours.WHITE};
  border-radius: 8px;
`
const AlertGroupPanel = styled.div`
  margin-top: 1.25rem;
  background-color: ${colours.WHITE};
  border: 1px solid ${colours.CLOUD_GREY};
  border-radius: 8px;
`
const Alert = styled.div`
  padding: 0.625rem 0.625rem 0.875rem 1rem;
  border-bottom: 1px solid ${colours.CLOUD_GREY};
  &:last-child {
    border-bottom: none;
  }
`
const AlertRule = styled.div<{ $dismissed: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.8125rem;
  font-weight: 400;
  line-height: 1.5rem;
  color: ${colours.LIGHT_NAVY};
  text-decoration: ${({ $dismissed }) =>
    $dismissed ? 'line-through' : 'none'};
`
const AlertDescription = styled.div<{ $dismissed: boolean }>`
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.375rem;
  color: ${({ $dismissed }) =>
    $dismissed ? colours.LIGHT_NAVY : colours.CORE_NAVY};
  text-decoration: ${({ $dismissed }) =>
    $dismissed ? 'line-through' : 'none'};
`
const CustomMessage = styled.div`
  display: inline-block;
`
